import React from "react";
import { ChildrenProps, dateDiff } from "@hex-insights/core";
import { usePermissionCheck } from "@hex-insights/permissioning";
import { permissions } from "../../../../Utilities";

export type MessageUpdatePermissionRequiredProps = {
	isAuthor: boolean;
	createdAt: string;
} & Partial<ChildrenProps>;

export function MessageUpdatePermissionRequired({
	isAuthor,
	createdAt,
	children,
}: MessageUpdatePermissionRequiredProps) {
	const hasPermission = useMessageUpdatePermissionCheck(isAuthor, createdAt);

	if (!hasPermission) {
		return null;
	}

	return <React.Fragment>{children}</React.Fragment>;
}

export function useMessageUpdatePermissionCheck(isAuthor: boolean, createdAt: string) {
	const customCheck = React.useCallback(
		(userPermissions: Record<string, boolean>) => {
			return isAuthor && userPermissions[permissions.Message.Update.Basic];
		},
		[isAuthor],
	);

	const baseHasPermission = usePermissionCheck({
		requiredPermission: permissions.Message.Update.Admin,
		customCheck,
		customCheckMethod: "or",
	});

	return baseHasPermission && dateDiff(new Date(), createdAt, "hours") < 24;
}

export type MessageDeletePermissionRequiredProps = {
	isAuthor: boolean;
} & Partial<ChildrenProps>;

export function MessageDeletePermissionRequired({ isAuthor, children }: MessageDeletePermissionRequiredProps) {
	const hasPermission = useMessageDeletePermissionCheck(isAuthor);

	if (!hasPermission) {
		return null;
	}

	return <React.Fragment>{children}</React.Fragment>;
}

export function useMessageDeletePermissionCheck(isAuthor: boolean) {
	const customCheck = React.useCallback(
		(userPermissions: Record<string, boolean>) => {
			return isAuthor && userPermissions[permissions.Message.Delete.Basic];
		},
		[isAuthor],
	);

	return usePermissionCheck({
		requiredPermission: permissions.Message.Delete.Admin,
		customCheck,
		customCheckMethod: "or",
	});
}
